import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { accessToken, endSession } from './cognito.ts';

// Whether anyone is signed in, and nothing else. Roles and identity come from the API's /v1/me
// (FND-01, ADR-0004 rule 1) — never from the token, which says who the identity provider
// authenticated and carries no AVenue permission truth.

export type SessionStatus = 'checking' | 'signed-in' | 'signed-out';

interface SessionValue {
  readonly status: SessionStatus;
  /** Re-reads the token. Called after a sign-in completes. */
  refresh(): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('checking');

  const refresh = useCallback(async () => {
    setStatus((await accessToken()) ? 'signed-in' : 'signed-out');
  }, []);

  const signOut = useCallback(async () => {
    await endSession();
    setStatus('signed-out');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({ status, refresh, signOut }),
    [status, refresh, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession() used outside a SessionProvider');
  return value;
}
